import React, { useCallback } from 'react';

import { FormattedMessage } from 'react-intl-next';

import { Inline } from '@atlaskit/primitives';
import { VerifiedTeamIcon } from '@atlassian/people-teams-ui/verified-team-icon';
import { type TeamListItemUiElement } from '@atlassian/ptc-common';

import { StyledOutlineLink } from '../Router';
import TeamAvatar from '../TeamElement/TeamAvatar';
import { WithTeamProfilecard } from '../WrappedWithProfilecard';

import { messages } from './messages';
import * as Styled from './TeamsProfileItem.styled';
import { type TeamsProfileItemProps } from './types';

const findMemberText = (
	isCurrentUser: boolean,
	team: Omit<TeamListItemUiElement, 'membershipSettings'>,
) => {
	let messageData;

	if (isCurrentUser) {
		messageData = messages.membersPlain;
	} else {
		messageData = team.includesYou ? messages.membersIncludingYou : messages.membersPlain;
	}

	return <FormattedMessage {...messageData} values={{ memberCount: team.memberCount }} />;
};

export default function TeamsProfileItem(props: TeamsProfileItemProps) {
	const { team, isCurrentUser, index, onClick } = props;

	const handleClick = useCallback(
		(e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
			if (onClick) {
				onClick(e, index!);
			}
		},
		[index, onClick],
	);

	return (
		<WithTeamProfilecard teamId={team.id} trigger="hover" triggerLinkType="none">
			<StyledOutlineLink onClick={handleClick} href={`team/${team.id}`} tabIndex={0}>
				<Styled.TeamsProfileItemWrapper tabIndex={-1}>
					<Styled.TeamsProfileItemAvatar>
						<TeamAvatar teamId={team.id} avatarUrl={team.smallAvatarImageUrl} />
					</Styled.TeamsProfileItemAvatar>
					<Styled.TeamsProfileItemBody>
						<Inline alignBlock="center">
							<Styled.TeamsProfileItemTeamName>{team.displayName}</Styled.TeamsProfileItemTeamName>
							{team.isVerified && <VerifiedTeamIcon showTooltip />}
						</Inline>
						<Styled.TeamsProfileItemTeamMembers>
							{findMemberText(isCurrentUser, team)}
						</Styled.TeamsProfileItemTeamMembers>
					</Styled.TeamsProfileItemBody>
				</Styled.TeamsProfileItemWrapper>
			</StyledOutlineLink>
		</WithTeamProfilecard>
	);
}
